import { Check } from '@mui/icons-material'
import { Components, styled, Theme } from '@mui/material'

declare module '@mui/material/Checkbox' {
  interface CheckboxProps {
    'data-is-ai-prefilled'?: boolean
  }
}

const iconClassName = 'checkbox-icon'

// Hover state for a given checkbox state, including label hover when the
// checkbox is wrapped in a FormControlLabel.
const hoverSelector = (stateClass = '') =>
  `&${stateClass}:hover:not(.Mui-disabled) input ~ .${iconClassName}, label:hover &${stateClass}:not(.Mui-disabled) input ~ .${iconClassName}`

const BaseIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  borderRadius: theme.shape['radius-sm'],
  borderStyle: 'solid',
  borderWidth: theme.strokeWidth.strong,
  width: '18px',
  height: '18px',
}))

const CheckedIcon = () => (
  <BaseIcon className={iconClassName}>
    {/* TODO - nucleo icon */}
    <Check sx={{ width: '100%', height: '100%' }} />
  </BaseIcon>
)
const IndeterminateIcon = () => (
  <BaseIcon className={iconClassName}>
    {/* TODO - nucleo icon? */}
    <svg xmlns="http://www.w3.org/2000/svg" width="10" viewBox="0 0 10 2" fill="none">
      <path
        d="M9 0C9.55229 0 10 0.447715 10 1C10 1.55228 9.55229 2 9 2H1C0.447715 2 0 1.55228 0 1C0 0.447715 0.447715 0 1 0H9Z"
        fill="currentColor"
      />
    </svg>
  </BaseIcon>
)

export const MuiCheckbox: Components<Omit<Theme, 'components'>>['MuiCheckbox'] = {
  defaultProps: {
    disableRipple: true,
    icon: <BaseIcon className={iconClassName} />,
    checkedIcon: <CheckedIcon />,
    indeterminateIcon: <IndeterminateIcon />,
  },
  styleOverrides: {
    root: ({ theme, 'data-is-ai-prefilled': isAiPrefilled }) => ({
      padding: theme.spacing(2),

      '& ~ .MuiFormControlLabel-label': {
        // Combination of padding 2 above and margin 1 here gives spacing 3 between the visual part of the checkbox and the label
        marginLeft: theme.spacing(1),
      },

      '&.Mui-disabled': {
        opacity: 0.3,
      },

      [`& .${iconClassName}`]: {
        borderColor: theme.palette.border.strong,
      },

      [`&.Mui-checked .${iconClassName}`]: {
        backgroundColor: theme.palette.bg.brand.default,
        borderColor: theme.palette.bg.brand.default,
        color: theme.palette.fg.brand.onBrand,
      },
      [`&.MuiCheckbox-indeterminate .${iconClassName}`]: {
        color: theme.palette.border.strong,
      },

      // Hover state for unchecked (inc indeterminate) checkboxes
      [hoverSelector()]: {
        borderColor: theme.palette.border.strongest,
        color: theme.palette.border.strongest,
      },
      // Hover state for checked checkboxes
      [hoverSelector('.Mui-checked')]: {
        backgroundColor: theme.palette.bg.brand.hover,
        borderColor: theme.palette.bg.brand.hover,
        color: theme.palette.fg.brand.onBrand,
      },
      ...(isAiPrefilled && {
        [`&:not(.Mui-disabled) .${iconClassName}`]: {
          borderColor: theme.palette.bg.ai.default,
        },
        [`&.Mui-checked:not(.Mui-disabled) .${iconClassName}`]: {
          backgroundColor: theme.palette.bg.ai.default,
        },
        [`&.MuiCheckbox-indeterminate:not(.Mui-disabled) .${iconClassName}`]: {
          color: theme.palette.fg.ai.default,
        },
        [hoverSelector()]: {
          borderColor: theme.palette.bg.ai.strongest,
        },
        [hoverSelector('.Mui-checked')]: {
          backgroundColor: theme.palette.bg.ai.strongest,
        },
        [hoverSelector('.MuiCheckbox-indeterminate')]: {
          color: theme.palette.fg.ai.strongest,
        },
      }),
    }),
  },
}
